// Display-currency preference. Amounts are always calculated in USD and
// converted at display time using the rate stored in the browser. The rate
// is stored together with where it came from (live, a past date, or typed
// in), so everything on screen is converted the same way and can say how.
import { apiRequest } from './apiClient';
import { toIsoDate, formatLongDate, formatRateNumber } from './historicalRates';

export const SUPPORTED_CURRENCIES = [
  { code: 'USD', label: 'USD (US Dollar)' },
  { code: 'IDR', label: 'IDR (Indonesian Rupiah)' }
];

// How the exchange rate is chosen. "manual" is a rate the user typed in.
export const RATE_MODES = [
  { value: 'live', label: 'Live' },
  { value: 'historical', label: 'Historical' },
  { value: 'manual', label: 'Custom' }
];

const DEFAULT_IDR_RATE = 15000;
const LIVE_RATE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const KEYS = {
  code: 'currency',
  rate: 'currencyRate',
  mode: 'currencyRateMode',
  // Written by versions that only had a "use live rate" switch
  legacyUseLive: 'useLiveCurrencyRate',
  fetchedAt: 'currencyRateFetchedAt',
  // Historical rates: the day that was asked for and the day the rate belongs to
  requestedDate: 'currencyRateRequestedDate',
  rateDate: 'currencyRateDate',
  source: 'currencyRateSource'
};

const listeners = new Set();

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (e) {}
  return null;
};

const readItem = (key) => {
  const storage = getStorage();
  if (!storage) return null;
  try { return storage.getItem(key); } catch (e) { return null; }
};

const writeItem = (key, value) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    if (value === null || value === undefined) storage.removeItem(key);
    else storage.setItem(key, String(value));
  } catch (e) {}
};

const isSupported = (code) => SUPPORTED_CURRENCIES.some((c) => c.code === code);

export const getCurrencyConfig = () => {
  const storedCode = readItem(KEYS.code);
  const code = isSupported(storedCode) ? storedCode : 'USD';

  // The stored rate belongs to the non-USD currency; USD is never converted.
  if (code === 'USD') return { code, rate: 1 };

  let rate = 1;
  const parsedRate = parseFloat(readItem(KEYS.rate));
  if (!isNaN(parsedRate) && parsedRate > 0) rate = parsedRate;
  if (code === 'IDR' && rate === 1) rate = DEFAULT_IDR_RATE;

  return { code, rate };
};

export const liveRatesAvailable = () =>
  !(typeof window !== 'undefined' && window.ENV_VARS && window.ENV_VARS.EXCHANGE_RATE_ENABLED === false);

// The saved rate mode. Without one, live rates are the default.
export const getRateMode = () => {
  const stored = readItem(KEYS.mode);
  if (stored === 'historical') return readItem(KEYS.rateDate) ? 'historical' : 'manual';
  if (RATE_MODES.some((mode) => mode.value === stored)) return stored;

  const legacy = readItem(KEYS.legacyUseLive);
  if (legacy === 'true') return 'live';
  if (legacy === 'false' || readItem(KEYS.rate)) return 'manual';
  return liveRatesAvailable() ? 'live' : 'manual';
};

export const getCurrencySettings = () => {
  const { code } = getCurrencyConfig();
  const mode = getRateMode();
  const rawFetchedAt = readItem(KEYS.fetchedAt);
  let fetchedAt = null;
  if (rawFetchedAt) {
    // Stored as epoch milliseconds; older builds may have stored other formats.
    const asNumber = Number(rawFetchedAt);
    const date = new Date(isNaN(asNumber) ? rawFetchedAt : asNumber);
    if (!isNaN(date.getTime())) fetchedAt = date;
  }
  return {
    code,
    rate: readItem(KEYS.rate) || '',
    mode,
    fetchedAt: mode === 'live' ? fetchedAt : null,
    requestedDate: mode === 'historical' ? readItem(KEYS.requestedDate) || readItem(KEYS.rateDate) : null,
    rateDate: mode === 'historical' ? readItem(KEYS.rateDate) : null,
    source: mode === 'historical' ? readItem(KEYS.source) || '' : ''
  };
};

const notify = () => listeners.forEach((listener) => listener());

export const subscribeCurrency = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// A stable primitive that changes whenever the display currency, the rate or
// where the rate came from does.
export const getCurrencySnapshot = () => {
  const { code, rate } = getCurrencyConfig();
  if (code === 'USD') return `${code}:${rate}`;
  const { mode, requestedDate, rateDate, fetchedAt } = getCurrencySettings();
  const origin = mode === 'historical' ? `${requestedDate}>${rateDate}` : fetchedAt ? fetchedAt.getTime() : '';
  return `${code}:${rate}:${mode}:${origin}`;
};

export const setCurrencyCode = (code) => {
  if (!isSupported(code)) return;
  writeItem(KEYS.code, code);
  notify();
};

// The rate and its origin are always saved together, so the mode can never
// describe a rate that came from somewhere else.
const storeRate = (mode, rate, { fetchedAt = null, requestedDate = null, rateDate = null, source = null } = {}) => {
  writeItem(KEYS.mode, mode);
  writeItem(KEYS.rate, rate === '' ? null : rate);
  writeItem(KEYS.fetchedAt, fetchedAt ? fetchedAt.getTime() : null);
  writeItem(KEYS.requestedDate, requestedDate);
  writeItem(KEYS.rateDate, rateDate);
  writeItem(KEYS.source, source);
  notify();
};

// A rate typed in by the user.
export const setManualRate = (rate) => storeRate('manual', rate);

// Fetches the current rate for `code` through the backend (the API key stays
// on the server) and switches to it. Returns the rate.
export const fetchLiveRate = async (code) => {
  const data = await apiRequest(`/api/exchange-rate?currency=${encodeURIComponent(code)}`);
  if (!data || !(data.rate > 0)) throw new Error('The exchange rate service returned an invalid rate.');
  storeRate('live', String(data.rate), { fetchedAt: new Date() });
  return data.rate;
};

// Fetches the USD rate for `code` on `date` (YYYY-MM-DD) and switches to it.
// When nothing was published that day the backend answers with the most
// recent earlier business day; both dates are kept. Returns the backend's answer.
export const fetchHistoricalRate = async (code, date) => {
  const params = new URLSearchParams({ from: 'USD', to: code, date, today: toIsoDate(new Date()) });
  const data = await apiRequest(`/api/exchange-rate/history?${params.toString()}`);
  if (!data || !(data.rate > 0) || !data.rateDate) {
    throw new Error('The exchange rate service returned an invalid rate.');
  }
  storeRate('historical', String(data.rate), {
    requestedDate: data.requestedDate || date,
    rateDate: data.rateDate,
    source: data.source
  });
  return data;
};

// Keeps the live rate honest: refreshes the stored rate when it is old.
export const refreshLiveRateIfStale = async () => {
  const { code, mode, fetchedAt } = getCurrencySettings();
  if (mode !== 'live' || code === 'USD') return false;
  if (fetchedAt && Date.now() - fetchedAt.getTime() < LIVE_RATE_MAX_AGE_MS) return false;
  try {
    await fetchLiveRate(code);
    return true;
  } catch (e) {
    return false;
  }
};

const formatFetchTime = (date) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(date);

// One sentence saying how amounts are being converted: the kind of rate, the
// rate itself and the date it belongs to. Empty for USD, which is not converted.
export const describeRate = () => {
  const { code, rate } = getCurrencyConfig();
  if (code === 'USD') return '';

  const settings = getCurrencySettings();
  const pair = `1 USD = ${formatRateNumber(rate)} ${code}`;
  if (!(parseFloat(settings.rate) > 0)) {
    return settings.mode === 'live'
      ? `Converted at the default rate, ${pair}, until the live rate loads.`
      : `Converted at the default rate, ${pair}.`;
  }
  if (settings.mode === 'live') {
    return `Converted at the live rate, ${pair}${settings.fetchedAt ? `, updated ${formatFetchTime(settings.fetchedAt)}` : ''}.`;
  }
  if (settings.mode === 'historical') {
    const sourceNote = settings.source ? ` (${settings.source})` : '';
    const sentence = `Converted at the historical rate for ${formatLongDate(settings.rateDate)}, ${pair}${sourceNote}.`;
    return settings.requestedDate && settings.requestedDate !== settings.rateDate
      ? `${sentence} No rate was published for ${formatLongDate(settings.requestedDate)}, so the most recent earlier business day is used.`
      : sentence;
  }
  return `Converted at a custom rate, ${pair}.`;
};

const getLocale = (code) => (code === 'IDR' ? 'id-ID' : 'en-US');

export const formatCurrency = (value) => {
  const numericValue = typeof value === 'number' && isFinite(value) ? value : 0;
  const { code, rate } = getCurrencyConfig();
  return new Intl.NumberFormat(getLocale(code), { style: 'currency', currency: code }).format(numericValue * rate);
};

// Short form for chart axes and bar labels, e.g. "$828", "$1.2K" or "Rp 97 jt".
export const formatCurrencyCompact = (value) => {
  const numericValue = typeof value === 'number' && isFinite(value) ? value : 0;
  const { code, rate } = getCurrencyConfig();
  const converted = numericValue * rate;
  // Below a thousand there is nothing to abbreviate: show whole units
  const isSmall = Math.abs(converted) < 1000;
  return new Intl.NumberFormat(getLocale(code), {
    style: 'currency',
    currency: code,
    notation: isSmall ? 'standard' : 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: isSmall ? 0 : 1
  }).format(converted);
};
